import { createClass as _createClass, toConsumableArray as _toConsumableArray, classCallCheck as _classCallCheck, objectSpread2 as _objectSpread2 } from '../../_virtual/_rollupPluginBabelHelpers.js';
import { noop } from './util/type.js';
import { HorizontalSwipeTracker } from './SwipeController.js';
import { CachedCSSProperty as CachedCSSProperty$1 } from './util/dom/CachedCSSProperty.js';
import { CachedDOMMeasurements as CachedDOMMeasurements$1 } from './util/dom/CachedDOMMeasurements.js';
import { ItemTracker as ItemTracker$1 } from './ItemTracker.js';
import { FilmstripPageChangeType, FilmstripNavigationCause } from './PageChange.js';
import { PageTracker as PageTracker$1 } from './PageTracker.js';
import { A11yHider } from './util/a11yUtils.js';
import { A11yTracker as A11yTracker$1 } from './A11yTracker.js';
import { transitionEndEventName } from './util/animation.js';
import { CachedWidth as CachedWidth$1 } from './util/dom/CachedWidth.js';
import { CachedInnerWidth as CachedInnerWidth$1 } from './util/dom/CachedInnerWidth.js';
import { removeEvents, attachEvents } from './util/binder.js';
import debounce$1 from '../../utilities/debounce/debounce.js';

var back = FilmstripPageChangeType.back,
    remain = FilmstripPageChangeType.remain,
    forward = FilmstripPageChangeType.forward;
var FilmstripController = /*#__PURE__*/function () {
  function FilmstripController(el, optionsPassed) {
    var _this = this;

    _classCallCheck(this, FilmstripController);

    this.el = el; //
    // Constants
    //

    /** This is the distance in pixels you need to swipe, in either direction, to trigger a page flip */

    this.pageSwipeThreshold = 50; //
    // Properties and getters
    //

    /** Are we in the middle of an animation? Let's keep track */

    this.animating = false;

    this.show = function () {
      if (_this.el.classList.contains('hide')) _this.el.classList.remove('hide');
    };

    this.hide = function () {
      return _this.el.classList.add('hide');
    };

    this.attachNavButtonEvents = function () {
      return attachEvents({
        internal: _this.navButtonEventBindings
      });
    };

    this.removeNavButtonEvents = function () {
      return removeEvents({
        internal: _this.navButtonEventBindings
      });
    };
    /** Adjusts the filmstrip's accounting according to window and item size. call this if ever one of
    the other changes. */


    this.calibrate = function () {
      if (!_this.el) {
        /* eslint-disable no-console */
        console.log('WARNING: no element exists for this filmstrip. Something is very wrong...skipping calibration.');
        return;
      }

      _this.clearCache();

      _this.pages.calculate();

      _this.a11y.setAttrs();

      _this.updateNavVisibility();

      _this.options.onCalibrate(_this);
    };
    /** Show a little animated hint that you can swipe the filmstrip. */


    this.hint = function () {
      _this.el.setAttribute('hinted', '');

      setTimeout(function () {
        return _this.el.removeAttribute('hinted');
      }, 1500);
    }; // Event handlers
    // These will respond to various events.


    this.onSwipeMove = function (distance) {
      _this.updateTotalOffset(distance);
    };
    /** A swipe has begun! Engage styles for during the swipe. */


    this.onSwipeConfirmed = function () {
      return _this.el.dataset.swiping = 'true';
    };
    /** The swipe is done! What to do? Flip the page, or snap back? */


    this.onSwipeEnd = function (distance, swipeThresholdMet) {
      if (!_this.isReady) return;

      _this.el.removeAttribute('data-swiping'); // If the swipe met the page-flip threshold, change pages in the appropriate direction. If the
      // swipe threshold was not met, reset the offset to zero


      var delta = _this.getDelta(distance);

      if (swipeThresholdMet && delta !== remain) {
        _this.pages.setPage(delta, FilmstripNavigationCause.swipe);
      } else {
        _this.updateTotalOffset(0);
      }
    };
    /** MutationObserver subscriber callback function. This ensures that pages and a11y and everything
        are recalculated after a change to the filmstrip's children. */


    this.onMutation = function () {
      _this.calibrate();
    };

    this.afterTransition = function (immediate, callback) {
      var eventTarget = _this.items.firstRegularItem;

      var doIt = function doIt() {
        _this.animating = false;
        callback();
        eventTarget.removeEventListener(transitionEndEventName, doIt);
      };

      _this.animating = true;

      if (immediate || _this.items.filmstripIsEmpty) {
        // just do it
        doIt();
        return;
      }

      eventTarget.addEventListener(transitionEndEventName, doIt);
    }; // when the animation completes, update all the a11y things


    this._onPageChanged = function (_ref) {
      var pageChange = _ref.detail;
      // Call the external pageWillChange callback. If it returns false, don't change the page.
      if (_this.options.onPageWillChange(pageChange) === false) return; // Update the tracker and set the correct page offset

      _this.updateTotalOffset(); // Update the page indicator, if available


      _this.updatePaginator(); // If either or both of the nav buttons shouldn't be shown, hide them!


      _this.updateNavVisibility();

      _this.afterTransition(pageChange.noChange, function () {
        // Update all a11y attributes and manage focus when the animation completes
        _this.a11y.setAttrs(pageChange.type); // Call the external onPageChanged callback, mainly to re-render children


        _this.options.onPageChanged(pageChange);
      });
    };

    this.onBackButtonClicked = function (event) {
      _this.onButtonClicked(back, event);
    };

    this.onForwardButtonClicked = function (event) {
      _this.onButtonClicked(forward, event);
    };

    this.onWindowResize = debounce$1(function () {
      _this.pages.resetToFirstPage(FilmstripNavigationCause.resize);

      _this.calibrate();
    }, 100); // UI updaters

    this.updateTotalOffset = function () {
      var distance = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 0;
      if (_this.el === null) return;

      var offset = _this.pages.getOffset() + _this.getContentMovement(distance);

      _this.el.style.setProperty('--item-total-offset', "".concat(offset, "px"));
    };

    this.updateNavVisibility = function () {
      var backButton = _this.backButton,
          forwardButton = _this.forwardButton,
          _this$pages = _this.pages,
          onFirst = _this$pages.onFirst,
          onLast = _this$pages.onLast;
      if (onFirst) A11yHider.hide(backButton);else A11yHider.show(backButton);
      if (onLast) A11yHider.hide(forwardButton);else A11yHider.show(forwardButton);
    };

    this.options = _objectSpread2({
      onPageWillChange: noop,
      onPageChanged: noop,
      onCalibrate: noop,
      hasVariableItemWidths: false
    }, optionsPassed); // Set up the measurements object

    this.measurements = new CachedDOMMeasurements$1({
      width: new CachedWidth$1(function () {
        return _this.el;
      }, 400),
      childContainerSpace: new CachedInnerWidth$1(function () {
        return _this.childContainer;
      }, 400),
      buttonWidth: new CachedWidth$1(function () {
        return _this.backButton;
      }, 22),
      // Below, we deliberately measure the internal value, with its default, rather than the
      // consumer-facing --filmstrip-button-dodge
      buttonDodge: new CachedCSSProperty$1('--button-dodge', function () {
        return _this.el;
      }, 22),
      paddingLeft: new CachedCSSProperty$1('padding-left', function () {
        return _this.el;
      }, 0)
    }); // Create a MutationObserver to handle mischievous children that appear and disappear at will

    this.observer = new MutationObserver(this.onMutation);
    /* Fire up the trackers! These delegate objects are in principle completely compatible with the
       original filmstrip prototype! */

    this.swipeTracker = new HorizontalSwipeTracker(this.el, this);
    this.items = new ItemTracker$1(this.children, this.options.hasVariableItemWidths);
    this.pages = new PageTracker$1(this.el, this.items, this.measurements);
    this.a11y = new A11yTracker$1(this.childContainer, this.items, this.pages); // Attach events

    attachEvents(this.eventBindings); // RELEASE THE MUTANTS
    // MDN docs: https://developer.mozilla.org/en-US/docs/Web/API/MutationObserver
    // A great article for reference: https://www.smashingmagazine.com/2019/04/mutationobserver-api-guide/

    this.observer.observe(this.childContainer, {
      childList: true,
      subtree: true
    });
    window.setTimeout(function () {
      return _this.calibrate();
    }, 0); // Temporary: hang a reference to the controller on the DOM object so we can inspect it and play
    // with it

    el.controller = this;
  }
  /** A reference to the container that holds the children. A "daycare" element...? Hm. */


  _createClass(FilmstripController, [{
    key: "destroy",
    value: function destroy() {
      this.swipeTracker.destroy();
      this.observer.disconnect();
      removeEvents(this.eventBindings);
    } // EVENT BINDINGS

  }, {
    key: "clearCache",

    /** Clear any cached DOM measurements. Note to self: this is starting to look like a pattern that
    could be encapsulated. */
    value: function clearCache() {
      this.measurements.clear();
      this.items.clearCache();
    }
  }, {
    key: "tuggingAgainstEdge",
    value: function tuggingAgainstEdge(distance) {
      var _this$pages2 = this.pages,
          onFirst = _this$pages2.onFirst,
          onLast = _this$pages2.onLast;
      var swiping = this.getSwipeDirections(distance);
      var againstLeftEdge = onFirst && swiping.right;
      var againstRightEdge = onLast && swiping.left;
      return againstLeftEdge || againstRightEdge;
    }
    /** Given a swipe of a certain distance, determine how much the content should move in response. */

  }, {
    key: "getContentMovement",
    value: function getContentMovement(distance) {
      // If the user is at the end of the content -- swiping against the left edge, on the first page,
      // or the right edge, on the last page, make it "tug" against the user's swipe by dividing the
      // swipe distance by some constant. This is roughly how spring physics works.
      var tuggingAgainstEdge = this.tuggingAgainstEdge(distance);
      return tuggingAgainstEdge ? distance / 7 : distance;
    }
  }, {
    key: "onButtonClicked",
    value: function onButtonClicked(direction, event) {
      event.preventDefault();
      event.stopPropagation();
      if (!this.animating) this.pages.setPage(direction, FilmstripNavigationCause.button);
    }
  }, {
    key: "updatePaginator",
    value: function updatePaginator() {
      var id = this.el.getAttribute('id');
      var pagination = document.querySelector("nicollet-filmstrip-pagination[for=".concat(id, "]"));

      if (pagination) {
        pagination.setAttribute('page', this.pages.currentPageIndex.toString() || '0');
      }
    } // Utility methods

    /** Given a swipe distance, decide in which direction the page should flip */

  }, {
    key: "getDelta",
    value: function getDelta(distance) {
      var _this$pages3 = this.pages,
          onLast = _this$pages3.onLast,
          onFirst = _this$pages3.onFirst;
      var swiping = this.getSwipeDirections(distance);
      if (!swiping.thresholdMet) return remain;
      if (swiping.right) return onFirst ? remain : back;else if (swiping.left) return onLast ? remain : forward;
      return remain;
    }
  }, {
    key: "getSwipeDirections",
    value: function getSwipeDirections(distance) {
      return {
        left: distance < 0,
        right: distance > 0,
        thresholdMet: Math.abs(distance) > this.pageSwipeThreshold
      };
    }
  }, {
    key: "getButton",
    value: function getButton(direction) {
      return this.el.querySelector("[data-direction=\"".concat(direction, "\"]"));
    }
  }, {
    key: "toJSON",
    value: function toJSON() {
      return {
        items: this.items.toJSON(),
        measurements: this.measurements.toJSON(),
        pages: this.pages.toJSON()
      };
    }
  }, {
    key: "childContainer",
    get: function get() {
      return this.el.children[1] || this.el.children[0]; // fallback to [0] if no nav buttons
    }
    /** REAL DOM ALIAS: children -- as in the actual child elements from outside. Expected by the
        ItemTracker. */

  }, {
    key: "children",
    get: function get() {
      return this.childContainer.children;
    }
  }, {
    key: "eventBindings",
    get: function get() {
      return {
        internal: [].concat(_toConsumableArray(this.navButtonEventBindings), [[this.el, 'pagechanged', this._onPageChanged]]),
        global: [['resize', this.onWindowResize]]
      };
    }
  }, {
    key: "navButtonEventBindings",
    get: function get() {
      var navButtonBindings = [];

      if (this.backButton) {
        navButtonBindings.push([this.backButton, 'click', this.onBackButtonClicked]);
      }

      if (this.forwardButton) {
        navButtonBindings.push([this.forwardButton, 'click', this.onForwardButtonClicked]);
      }

      return navButtonBindings;
    }
    /** Meant to be set from outside, by a view library that's managing lazy loading! This tells the
    filmstrip what its total number of items is. If we're lazy loading, the number of currently
    rendered items is usually less than the total number of available items. And how can we calculate
    how many pages we need without knowing how many total there are? */

  }, {
    key: "totalItems",
    set: function set(totalChildren) {
      this.items.virtualItems = totalChildren;
    },
    get: function get() {
      return this.items.virtualItems;
    }
  }, {
    key: "isReady",
    get: function get() {
      return this.pages.isReady;
    }
  }, {
    key: "forwardButton",
    get: function get() {
      return this.getButton('forward');
    }
  }, {
    key: "backButton",
    get: function get() {
      return this.getButton('back');
    }
  }]);

  return FilmstripController;
}();

export { FilmstripController };
